import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Swipeable card slider on mobile/tablet, elegant grid on large screens.
export default function CardSlider({ items, testPrefix, gridClass = "lg:grid-cols-3" }) {
    const trackRef = useRef(null);
    const [active, setActive] = useState(0);
    const PAD = 20;

    const onScroll = () => {
        const c = trackRef.current;
        if (!c) return;
        let best = 0;
        let bestDist = Infinity;
        Array.from(c.children).forEach((ch, i) => {
            const d = Math.abs(ch.offsetLeft - PAD - c.scrollLeft);
            if (d < bestDist) {
                bestDist = d;
                best = i;
            }
        });
        setActive(best);
    };

    const goTo = (i) => {
        const c = trackRef.current;
        if (!c) return;
        const idx = Math.max(0, Math.min(i, items.length - 1));
        const child = c.children[idx];
        if (child) c.scrollTo({ left: child.offsetLeft - PAD, behavior: "smooth" });
    };

    return (
        <>
            {/* Mobile / tablet — swipe slider with arrows + dots */}
            <div className="lg:hidden" data-testid={`${testPrefix}-slider`}>
                <div
                    ref={trackRef}
                    onScroll={onScroll}
                    className="relative flex gap-5 overflow-x-auto snap-x snap-mandatory no-scrollbar px-5 -mx-5"
                >
                    {items.map((node, i) => (
                        <div key={i} className="w-[84%] sm:w-[58%] shrink-0 snap-start">
                            {node}
                        </div>
                    ))}
                </div>
                <div className="mt-8 flex items-center justify-center gap-5">
                    <button
                        onClick={() => goTo(active - 1)}
                        aria-label="Previous"
                        data-testid={`${testPrefix}-slider-prev`}
                        className="w-10 h-10 rounded-full border border-burgundy/25 text-burgundy flex items-center justify-center transition-colors hover:bg-burgundy hover:text-cream disabled:opacity-40"
                        disabled={active === 0}
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-2">
                        {items.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => goTo(i)}
                                aria-label={`Go to slide ${i + 1}`}
                                data-testid={`${testPrefix}-slider-dot-${i}`}
                                className={`h-2 rounded-full transition-all duration-300 ${
                                    i === active ? "w-6 bg-burgundy" : "w-2 bg-burgundy/25"
                                }`}
                            />
                        ))}
                    </div>
                    <button
                        onClick={() => goTo(active + 1)}
                        aria-label="Next"
                        data-testid={`${testPrefix}-slider-next`}
                        className="w-10 h-10 rounded-full border border-burgundy/25 text-burgundy flex items-center justify-center transition-colors hover:bg-burgundy hover:text-cream disabled:opacity-40"
                        disabled={active === items.length - 1}
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Desktop — grid */}
            <div className={`hidden lg:grid gap-6 ${gridClass}`}>
                {items.map((node, i) => (
                    <div key={i} className="h-full">
                        {node}
                    </div>
                ))}
            </div>
        </>
    );
}
